import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/features/boe/components/PPE.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";

import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { NcepRankTower } from "../../../shared/ui/chart/charts/RankTower.jsx";
import {
  LineChartWithToggles,
  BudgetInflationChart,
} from "../../../shared/ui/chart/LineChartWithToggles.jsx";

/**
 * Firestore:
 * collection: publicSchools
 * doc: ppe
 *
 * Shape:
 * {
 *   "_id": "ppe",
 *   "2024-2025": {
 *     "NORTH CANAAN": { nce, adm, ncep, ncepRounded, basicContribution },
 *     ...
 *   },
 *   ...
 * }
 */
const PPE_COLLECTION = "publicSchools";
const PPE_DOC_ID = "ppe";

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function formatNumber(v, { decimals = 0 } = {}) {
  if (!isFiniteNumber(v)) return "";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(v);
}

function formatCurrency(v, { decimals = 0 } = {}) {
  if (!isFiniteNumber(v)) return "";
  return `$${formatNumber(v, { decimals })}`;
}

function parseEndYearFromYearKey(yearKey) {
  const m = String(yearKey).match(/^(\d{4})-(\d{4})$/);
  return m ? Number(m[2]) : null;
}

function parseStartYearFromYearKey(yearKey) {
  const m = String(yearKey).match(/^(\d{4})-(\d{4})$/);
  return m ? Number(m[1]) : null;
}

function normDistrictName(s) {
  return String(s ?? "")
    .trim()
    .toUpperCase();
}

function slugifyKey(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function uniqueCaseInsensitive(list) {
  const seen = new Set();
  const out = [];
  for (const item of list || []) {
    const k = normDistrictName(item);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
}

/**
 * Normalize inflation rates to decimals.
 * - If stored as 3.2 -> treat as 0.032
 * - If stored as 0.032 -> keep as-is
 */
function normalizeInflationRate(raw) {
  if (!isFiniteNumber(raw)) return null;

  const abs = Math.abs(raw);

  // If your Firestore stores percent points, you will see values like:
  // 2.9, 3.2, 0.4, -0.4, etc.
  //
  // If it stores decimals, you will see:
  // 0.029, 0.032, 0.004, -0.004, etc.
  //
  // Annual CPI rates almost never exceed 0.2 (20%) in modern US data,
  // so treat abs >= 0.2 as "percent points" to avoid interpreting 0.4 as 40%.
  if (abs >= 0.2) return raw / 100;

  // Also treat abs > 1 as percent points (covers 2.9, 3.2, etc.)
  if (abs > 1) return raw / 100;

  // Otherwise treat as already-decimal
  return raw;
}

export function PPE() {
  const excelExportRef = useRef(null);

  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Inflation rates (years map), same source as StudentEnrollment
  const [inflationByYear, setInflationByYear] = useState({});

  // ✅ Snapshot area toggle (table + filters only; charts stay outside)
  const [showPpeSnapshot, setShowPpeSnapshot] = useState(true);

  const [selectedYearKey, setSelectedYearKey] = useSharedFilter("PPE.jsx:PPE:selectedYearKey", "");
  const [search, setSearch] = useState("");

  // Chart toggles (independent)
  const [showNceChart, setShowNceChart] = useState(false);
  const [showAdmChart, setShowAdmChart] = useState(false);
  const [showNcepChart, setShowNcepChart] = useState(false);
  const [showNcepInflationChart, setShowNcepInflationChart] = useState(false);
  const [showNcepRankTower, setShowNcepRankTower] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);

      try {
        const [ppeSnap, inflSnap] = await Promise.all([
          getDoc(doc(db, PPE_COLLECTION, PPE_DOC_ID)),
          getDoc(doc(db, "common", "inflationByYear")),
        ]);

        if (!alive) return;

        // ----- PPE doc -----
        if (!ppeSnap.exists()) {
          console.warn(`PPE doc missing: ${PPE_COLLECTION}/${PPE_DOC_ID}`);
          setDocData(null);
          setSelectedYearKey.initialize("");
          return;
        }

        const data = ppeSnap.data();
        setDocData(data || null);

        // ----- Inflation doc -----
        if (!inflSnap.exists()) {
          console.warn("Inflation doc missing: common/inflationByYear");
          setInflationByYear({});
        } else {
          const inflData = inflSnap.data();
          setInflationByYear(inflData?.years || {});
        }

        // ----- year key selection -----
        const yearKeys = Object.keys(data || {})
          .filter((k) => /^\d{4}-\d{4}$/.test(k))
          .sort((a, b) => {
            const ea = parseEndYearFromYearKey(a);
            const eb = parseEndYearFromYearKey(b);
            if (ea == null && eb == null)
              return String(a).localeCompare(String(b));
            if (ea == null) return -1;
            if (eb == null) return 1;
            return ea - eb;
          });

        const latest = yearKeys.length ? yearKeys[yearKeys.length - 1] : "";
        setSelectedYearKey.initialize((prev) =>
          prev && yearKeys.includes(prev) ? prev : latest,
        );
      } catch (e) {
        console.error("Error loading PPE doc:", e);
        if (alive) {
          setDocData(null);
          setSelectedYearKey.initialize("");
          setInflationByYear({});
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const yearOptions = useMemo(() => {
    if (!docData) return [];
    return Object.keys(docData)
      .filter((k) => /^\d{4}-\d{4}$/.test(k))
      .sort((a, b) => {
        const ea = parseEndYearFromYearKey(a);
        const eb = parseEndYearFromYearKey(b);
        if (ea == null && eb == null) return String(a).localeCompare(String(b));
        if (ea == null) return -1;
        if (eb == null) return 1;
        return ea - eb;
      });
  }, [docData]);

  const rawRows = useMemo(() => {
    if (!docData || !selectedYearKey) return [];
    const yearObj = docData?.[selectedYearKey];
    if (!yearObj || typeof yearObj !== "object") return [];

    return Object.entries(yearObj).map(([districtName, v]) => {
      const isGlastonbury = normDistrictName(districtName) === "GLASTONBURY";

      return {
        districtName,
        nce: isFiniteNumber(v?.nce) ? v.nce : null,
        adm: isFiniteNumber(v?.adm) ? v.adm : null,
        ncep: isFiniteNumber(v?.ncep) ? v.ncep : null,
        ncepRounded: isFiniteNumber(v?.ncepRounded) ? v.ncepRounded : null,
        basicContribution: isFiniteNumber(v?.basicContribution)
          ? v.basicContribution
          : null,
        rowClassName: isGlastonbury ? "bg-yellow-200" : "",
      };
    });
  }, [docData, selectedYearKey]);

  const allYearKeysSorted = useMemo(() => {
    if (!docData) return [];
    return Object.keys(docData)
      .filter((k) => /^\d{4}-\d{4}$/.test(k))
      .sort((a, b) => {
        const sa = parseStartYearFromYearKey(a);
        const sb = parseStartYearFromYearKey(b);
        if (sa == null && sb == null) return String(a).localeCompare(String(b));
        if (sa == null) return -1;
        if (sb == null) return 1;
        return sa - sb;
      });
  }, [docData]);

  const chartYearKeys = useMemo(() => {
    return allYearKeysSorted.filter((k) => {
      const start = parseStartYearFromYearKey(k);
      return start != null && start >= 2008;
    });
  }, [allYearKeysSorted]);

  const chartLabels = useMemo(() => chartYearKeys, [chartYearKeys]);

  const allDistrictNames = useMemo(() => {
    if (!docData || allYearKeysSorted.length === 0) return [];
    const names = [];

    for (const yk of allYearKeysSorted) {
      const yearObj = docData?.[yk];
      if (yearObj && typeof yearObj === "object") {
        names.push(...Object.keys(yearObj));
      }
    }

    return uniqueCaseInsensitive(names).sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
  }, [docData, allYearKeysSorted]);

  const buildAllDistrictSeries = useCallback(
    (fieldKey, valueType) => {
      if (
        !docData ||
        chartYearKeys.length === 0 ||
        allDistrictNames.length === 0
      )
        return [];

      return allDistrictNames.map((districtName) => {
        const data = chartYearKeys.map((yk) => {
          const yearObj = docData?.[yk];
          const row = yearObj?.[districtName];
          const v = row?.[fieldKey];
          return typeof v === "number" && Number.isFinite(v) ? v : null;
        });

        return {
          key: slugifyKey(districtName),
          label: districtName,
          data,
          group: "districts",
          valueType,
          defaultVisible: normDistrictName(districtName) === "GLASTONBURY",
        };
      });
    },
    [docData, chartYearKeys, allDistrictNames],
  );

  const nceChart = useMemo(() => {
    return {
      labels: chartLabels,
      series: buildAllDistrictSeries("nce", "currency"),
    };
  }, [chartLabels, buildAllDistrictSeries]);

  const admChart = useMemo(() => {
    return {
      labels: chartLabels,
      series: buildAllDistrictSeries("adm", "count"),
    };
  }, [chartLabels, buildAllDistrictSeries]);

  const ncepChart = useMemo(() => {
    return {
      labels: chartLabels,
      series: buildAllDistrictSeries("ncep", "currency"),
    };
  }, [chartLabels, buildAllDistrictSeries]);

  const hasBasicContribution = useMemo(() => {
    return rawRows.some((r) => isFiniteNumber(r.basicContribution));
  }, [rawRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rawRows;
    return rawRows.filter((r) =>
      String(r.districtName).toLowerCase().includes(q),
    );
  }, [rawRows, search]);

  // -------- Glastonbury-only series (NCEP vs Inflation baseline) --------

  const glastonburyKeyByYear = useMemo(() => {
    if (!docData || chartYearKeys.length === 0) return {};

    const out = {};
    for (const yk of chartYearKeys) {
      const yearObj = docData?.[yk];
      if (!yearObj || typeof yearObj !== "object") continue;

      const found = Object.keys(yearObj).find(
        (name) => normDistrictName(name) === "GLASTONBURY",
      );

      if (found) out[yk] = found;
    }
    return out;
  }, [docData, chartYearKeys]);

  const glastonburyNcepSeries = useMemo(() => {
    if (!docData || chartYearKeys.length === 0) return [];

    return chartYearKeys.map((yk) => {
      const yearObj = docData?.[yk];
      const districtKey = glastonburyKeyByYear?.[yk];
      const row = districtKey ? yearObj?.[districtKey] : null;

      const v = row?.ncep;
      return typeof v === "number" && Number.isFinite(v) ? v : null;
    });
  }, [docData, chartYearKeys, glastonburyKeyByYear]);

  /**
   * Build the inflation baseline array aligned to chartYearKeys:
   * - Find first non-null NCEP -> anchor baseline to that value
   * - For each next year: baseline *= (1 + inflationRate)
   * - inflationRate is pulled from inflationByYear[startYear] and normalized
   */
  const glastonburyNcepInflationBaseline = useMemo(() => {
    if (!chartYearKeys.length) return [];

    const getRateDecimal = (startYear) => {
      const raw =
        inflationByYear?.[String(startYear)] ?? inflationByYear?.[startYear];
      return normalizeInflationRate(raw);
    };

    const out = new Array(chartYearKeys.length).fill(null);

    let base = null;
    let started = false;

    for (let i = 0; i < chartYearKeys.length; i++) {
      const yk = chartYearKeys[i];
      const startYear = parseStartYearFromYearKey(yk);
      const ncep = glastonburyNcepSeries[i];

      // Anchor baseline at first NCEP
      if (!started) {
        if (isFiniteNumber(ncep)) {
          base = ncep;
          started = true;
          out[i] = base;
        } else {
          out[i] = null;
        }
        continue;
      }

      // If baseline lost, try to re-anchor later
      if (!isFiniteNumber(base)) {
        if (isFiniteNumber(ncep)) {
          base = ncep;
          out[i] = base;
        } else {
          out[i] = null;
        }
        continue;
      }

      const rate = startYear != null ? getRateDecimal(startYear) : null;

      // If rate missing, keep flat rather than breaking the line
      if (isFiniteNumber(rate)) {
        base = base * (1 + rate);
      }

      out[i] = base;
    }

    return out;
  }, [chartYearKeys, glastonburyNcepSeries, inflationByYear]);

  const tableColumns = useMemo(() => {
    const cols = [
      {
        key: "districtName",
        label: "",
        style: { width: "200px", minWidth: "200px" },
        editable: false,
      },
      {
        key: "nce",
        label: "NCE",
        align: "right",
        style: { width: "120px", minWidth: "120px" },
        render: (row) => formatCurrency(row.nce, { decimals: 0 }),
      },
      {
        key: "adm",
        label: "Average Daily Membership (ADM)",
        align: "right",
        style: { width: "160px", minWidth: "160px" },
        render: (row) => formatNumber(row.adm, { decimals: 2 }),
      },
      {
        key: "ncep",
        label: "NCEP (Col 1 / Col 2)",
        align: "right",
        style: { width: "170px", minWidth: "170px" },
        render: (row) => formatCurrency(row.ncep, { decimals: 2 }),
      },
      {
        key: "ncepRounded",
        label: "State Agency Placement Basic Contribution (Col 3 Rounded)",
        align: "right",
        style: { width: "260px", minWidth: "260px" },
        render: (row) => formatCurrency(row.ncepRounded, { decimals: 0 }),
      },
    ];

    if (hasBasicContribution) {
      cols.push({
        key: "basicContribution",
        label:
          "Local Initialized Placement Basic Contribution (Col 3 × 4.5, Rounded)",
        align: "right",
        style: { width: "290px", minWidth: "290px" },
        render: (row) => formatCurrency(row.basicContribution, { decimals: 0 }),
      });
    }

    return cols;
  }, [hasBasicContribution]);

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Loading PPE…</div>;
  }

  if (!docData || !selectedYearKey || yearOptions.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        PPE data is not available.
      </div>
    );
  }

  return (
    <TableSearchScope>
    <div className="space-y-8">
      {/* ✅ Snapshot block (collapsible) */}
      <div className="space-y-3">
        <HeaderToggleButton
          isOpen={showPpeSnapshot}
          onToggle={() => setShowPpeSnapshot((prev) => !prev)}
          title="Per Pupil Expenditure (PPE)"
          ariaLabel="Toggle PPE snapshot"
          controlsId="ppe-snapshot"
          rightSlot={
            yearOptions.length > 0 ? (
              <div
                className="ml-auto flex flex-wrap items-center justify-end gap-2 pr-2 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Excel export button (unstyled icon) */}
                <button
                  type="button"
                  className="inline-flex items-center justify-center bg-transparent border-0 p-0 m-0 text-gray-700 hover:text-gray-900 focus:outline-none leading-none"
                  style={{ lineHeight: 1 }}
                  title="Download table to Excel"
                  aria-label="Download table to Excel"
                  onClick={(e) => {
                    e.stopPropagation();
                    excelExportRef.current?.exportToExcel?.();
                  }}
                >
                  <span className="relative top-[1px] text-base">💾</span>
                </button>

                {showPpeSnapshot && <SectionTableSearch />}
                <span className="shrink-0 whitespace-nowrap font-semibold text-xs text-gray-700">
                  Year
                </span>

                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={selectedYearKey || ""}
                  onChange={(e) => setSelectedYearKey(e.target.value)}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            ) : null
          }
        />

        {showPpeSnapshot && (
          <div id="ppe-snapshot" className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-gray-600">
                Net Current Expenditure per Pupil (NCEP) values are pulled from
                CT Dept of Education
              </p>

              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs">Search</span>
                <input
                  className="border rounded px-2 py-1 text-xs bg-white w-[240px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Type a district name…"
                />
              </div>
            </div>

            <CommonTable
              excelExportRef={excelExportRef}
              excelFilename={`ppe-${selectedYearKey || "latest"}.xlsx`}
              excelSheetName="PPE"
              excelTitle={`Per Pupil Expenditure (PPE) – ${selectedYearKey || "Latest"}`}
              title={null}
              columns={tableColumns}
              data={filteredRows}
              isEditing={false}
              headerColor="bg-[#e5eef9]"
              showAggregateTotal={false}
              defaultSortField="ncep"
              defaultSortOrder="desc"
            />
          </div>
        )}
      </div>

      {/* ✅ Charts remain available even when snapshot is collapsed */}
      <div className="space-y-6">
        {/* NCE Chart */}
        {nceChart.labels.length > 0 && nceChart.series.length > 0 && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showNceChart}
              onToggle={() => setShowNceChart((p) => !p)}
              title="PPE – Net Current Expenditure (NCE) by District (Over Time)"
              ariaLabel="Toggle NCE chart"
              controlsId="ppe-nce-chart"
            />

            {showNceChart && (
              <div id="ppe-nce-chart">
                <LineChartWithToggles
                  labels={nceChart.labels}
                  series={nceChart.series}
                  showTotalWhenNoneSelected={false}
                  showSeriesToggles={true}
                  toggleUIMode="search"
                  searchDefaultText="Glastonbury"
                  searchPlaceholder="Glastonbury"
                  showChartTypeSelector={true}
                  defaultYAxisMode="zero"
                />
              </div>
            )}
          </div>
        )}

        {/* ADM Chart */}
        {admChart.labels.length > 0 && admChart.series.length > 0 && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showAdmChart}
              onToggle={() => setShowAdmChart((p) => !p)}
              title="PPE – Average Daily Membership (ADM) by District (Over Time)"
              ariaLabel="Toggle ADM chart"
              controlsId="ppe-adm-chart"
            />

            {showAdmChart && (
              <div id="ppe-adm-chart">
                <LineChartWithToggles
                  labels={admChart.labels}
                  series={admChart.series}
                  showTotalWhenNoneSelected={false}
                  showSeriesToggles={true}
                  toggleUIMode="search"
                  searchDefaultText="Glastonbury"
                  searchPlaceholder="Glastonbury"
                  showChartTypeSelector={true}
                  defaultYAxisMode="zero"
                />
              </div>
            )}
          </div>
        )}

        {/* NCEP Chart */}
        {ncepChart.labels.length > 0 && ncepChart.series.length > 0 && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showNcepChart}
              onToggle={() => setShowNcepChart((p) => !p)}
              title="PPE – NCEP (NCE / ADM) by District (Over Time)"
              ariaLabel="Toggle NCEP chart"
              controlsId="ppe-ncep-chart"
            />

            {showNcepChart && (
              <div id="ppe-ncep-chart">
                <LineChartWithToggles
                  labels={ncepChart.labels}
                  series={ncepChart.series}
                  showTotalWhenNoneSelected={false}
                  showSeriesToggles={true}
                  toggleUIMode="search"
                  searchDefaultText="Glastonbury"
                  searchPlaceholder="Glastonbury"
                  showChartTypeSelector={true}
                  defaultYAxisMode="zero"
                />
              </div>
            )}
          </div>
        )}

        {/* ✅ Glastonbury NCEP vs Inflation (BudgetInflationChart style) */}
        {chartLabels.length > 0 &&
          glastonburyNcepSeries.some(isFiniteNumber) &&
          glastonburyNcepInflationBaseline.some(isFiniteNumber) && (
            <div className="space-y-1">
              <HeaderToggleButton
                isOpen={showNcepInflationChart}
                onToggle={() => setShowNcepInflationChart((p) => !p)}
                title="PPE – Glastonbury NCEP vs Inflation (Over Time)"
                ariaLabel="Toggle NCEP vs inflation chart"
                controlsId="ppe-ncep-inflation-chart"
              />

              {showNcepInflationChart && (
                <div id="ppe-ncep-inflation-chart">
                  <BudgetInflationChart
                    labels={chartLabels}
                    approvedData={glastonburyNcepSeries}
                    actualData={glastonburyNcepSeries}
                    inflationData={glastonburyNcepInflationBaseline}
                    defaultMode="approved"
                    showModeToggle={false}
                    approvedLabel="NCEP"
                    actualLabel="NCEP"
                  />
                </div>
              )}
            </div>
          )}

        {/* ✅ NCEP Rank Tower (selected year vs all districts) */}
        {rawRows.length > 0 && (
          <div className="space-y-1">
            <HeaderToggleButton
              isOpen={showNcepRankTower}
              onToggle={() => setShowNcepRankTower((p) => !p)}
              title="PPE – NCEP Rank Tower (Selected Year)"
              ariaLabel="Toggle NCEP rank tower"
              controlsId="ppe-ncep-rank-tower"
            />

            {showNcepRankTower && (
              <div id="ppe-ncep-rank-tower">
<NcepRankTower
  title=""
  yearOptions={yearOptions}
  selectedYearKey={selectedYearKey}
  onYearChange={(yk) => setSelectedYearKey(yk)}
  rows={rawRows}
  highlightLabel="Glastonbury"
  showHighlightSummary={false}
  useScroll={false}
/>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </TableSearchScope>
  );
}

export default PPE;
